import React, { useCallback, useEffect, useRef, useState } from "react";
import { Alert, Button, Form } from "react-bootstrap";
import { Link } from "react-router-dom";
import "../css/Signup.css";
import api from "../api/api";
import SignupCaptcha from "./SignupCaptcha";

const TRIAL_START_TOKEN_STORAGE_KEY = "plasticise_signup_trial_start_token";

const disciplines = [
  "Physiotherapist", "Occupational Therapist", "Speech Pathologist",
        "Exercise Physiologist", "Dietitian", "Podiatrist", "Chiropractor",
        "Osteopath", "Orthotist", "Audiologist", "Orthoptist",
];

const Signup = () => {
  const [details, setDetails] = useState(() => ({
    first_name: "", last_name: "", email: "", discipline: "",
    practice_name: "",
  }));
  const [errors, setErrors] = useState({});
  const [message, setMessage] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [captchaConfig, setCaptchaConfig] = useState(null);
  const [captchaConfigError, setCaptchaConfigError] = useState(false);
  const [configAttempt, setConfigAttempt] = useState(0);
  const [captchaToken, setCaptchaToken] = useState("");
  const [captchaAttempt, setCaptchaAttempt] = useState(0);
  const updateCaptcha = useCallback((token) => setCaptchaToken(token), []);
  const submitting = useRef(false);
  const feedback = useRef(null);
  const trialStartRequest = useRef(null);
  const trialStartToken = useRef(
    localStorage.getItem(TRIAL_START_TOKEN_STORAGE_KEY)
  );
  const trialStartTokenValidated = useRef(false);

  useEffect(() => {
    let cancelled = false;
    setCaptchaConfigError(false);
    api.get("/signup/", { timeout: 10000 }).then(({ data }) => {
      if (typeof data?.captcha_required !== "boolean" ||
          (data.captcha_required && !data.captcha_site_key)) {
        throw new Error("Verification configuration unavailable.");
      }
      if (!cancelled) setCaptchaConfig(data);
    }).catch(() => { if (!cancelled) setCaptchaConfigError(true); });
    return () => { cancelled = true; };
  }, [configAttempt]);

  useEffect(() => {
    if (message || isSuccess) {
      feedback.current?.focus();
      feedback.current?.scrollIntoView?.({ block: "nearest" });
    }
  }, [message, isSuccess]);

  const ensureTrialStarted = async () => {
    if (trialStartTokenValidated.current && trialStartToken.current) {
      return trialStartToken.current;
    }

    if (trialStartRequest.current) {
      return trialStartRequest.current;
    }

    const request = api
      .post(
        "/signup/start-trial/",
        trialStartToken.current
          ? { trial_start_token: trialStartToken.current }
          : {}
      )
      .then((response) => {
        const token = response.data?.trial_start_token;
        if (!token) {
          throw new Error("Trial start token was not returned.");
        }

        trialStartToken.current = token;
        trialStartTokenValidated.current = true;
        localStorage.setItem(TRIAL_START_TOKEN_STORAGE_KEY, token);
        return token;
      })
      .finally(() => {
        trialStartRequest.current = null;
      });

    trialStartRequest.current = request;
    return request;
  };

  const updateField = (event) => {
    const { name, value } = event.target;
    setDetails((current) => ({ ...current, [name]: value }));
    setErrors((current) => ({ ...current, [name]: undefined }));

    if (String(value).trim()) {
      ensureTrialStarted().catch(() => {
        // Do not interrupt typing. Submission retries and surfaces any failure.
      });
    }
  };

  const fieldProps = (name) => ({
    name, value: details[name], onChange: updateField,
    isInvalid: Boolean(errors[name]),
    "aria-describedby": errors[name] ? `${name}-error` : undefined,
  });

  const fieldError = (name) => (
    <Form.Control.Feedback type="invalid" id={`${name}-error`}>
      {Array.isArray(errors[name]) ? errors[name].join(" ") : errors[name]}
    </Form.Control.Feedback>
  );

  const handleSignup = async (event) => {
    event.preventDefault();
    if (submitting.current) return;
    if (!captchaConfig || (captchaConfig.captcha_required && !captchaToken)) {
      setMessage("Please complete the robot check before starting your trial.");
      return;
    }
    submitting.current = true;
    setIsSubmitting(true);
    setErrors({});
    setMessage("");
    try {
      const token = await ensureTrialStarted();
      await api.post("/signup/", {
        ...details,
        first_name: details.first_name.trim(),
        last_name: details.last_name.trim(),
        email: details.email.trim().toLowerCase(),
        practice_name: details.practice_name.trim(),
        trial_start_token: token,
        ...(captchaConfig.captcha_required ? { captcha_token: captchaToken } : {}),
      });
      localStorage.removeItem(TRIAL_START_TOKEN_STORAGE_KEY);
      trialStartToken.current = null;
      trialStartTokenValidated.current = false;
      setIsSuccess(true);
    } catch (error) {
      const data = error.response?.data;
      if (data && typeof data === "object") {
        setErrors(data);
        setMessage(data.detail || "Please check the highlighted details and try again.");
      } else {
        setMessage("We couldn't complete signup. Check your connection and try again.");
      }
    } finally {
      submitting.current = false;
      setIsSubmitting(false);
      // Tokens are single-use; every retry needs a fresh challenge.
      setCaptchaToken("");
      setCaptchaAttempt((value) => value + 1);
    }
  };

  return (
    <main className="trial-signup-page">
      <section className="trial-signup-card" aria-labelledby="trial-signup-title">
      <Link to="/" className="trial-signup-close" aria-label="Back to login">×</Link>
      <header className="trial-signup-header">
        <h1 id="trial-signup-title" className="font-mynor-extra-bold">Ready to make the next program personal?</h1>
        <p className="trial-signup-trial">14 days free. Unlimited access.</p>
      </header>

      {isSuccess ? (
        <div ref={feedback} tabIndex={-1}>
          <Alert variant="success">
            <Alert.Heading>Check your email</Alert.Heading>
            <p className="mb-0">
              We've sent a link to <strong className="text-break">{details.email.trim().toLowerCase()}</strong>.
              {" "}Set your password, then log in to review the terms and start your guided onboarding.
            </p>
          </Alert>
          <p>Can't find it? Check your spam folder or <Link to="/reset-password">request another password setup link</Link>.</p>
        </div>
      ) : (
        <div className="trial-signup-layout"><div className="trial-signup-form"><Form onSubmit={handleSignup} aria-label="Therapist signup">
          {message && (
            <div ref={feedback} tabIndex={-1}>
              <Alert variant="danger">{message}</Alert>
            </div>
          )}
          <fieldset disabled={isSubmitting}>
            <div className="row">
              <Form.Group className="col-12 col-sm-6 mb-3" controlId="signup-first-name">
                <Form.Label>First name</Form.Label>
                <Form.Control {...fieldProps("first_name")} placeholder="Jane" autoComplete="given-name" maxLength={30} required />
                {fieldError("first_name")}
              </Form.Group>
              <Form.Group className="col-12 col-sm-6 mb-3" controlId="signup-last-name">
                <Form.Label>Last name</Form.Label>
                <Form.Control {...fieldProps("last_name")} placeholder="Smith" autoComplete="family-name" maxLength={30} required />
                {fieldError("last_name")}
              </Form.Group>
            </div>
            <Form.Group className="mb-3" controlId="signup-email">
              <Form.Label>Email</Form.Label>
              <Form.Control {...fieldProps("email")} type="email" placeholder="you@clinic.com" autoComplete="email" maxLength={254} required />
              {fieldError("email")}
            </Form.Group>
            <Form.Group className="mb-3" controlId="signup-discipline">
              <Form.Label>Profession</Form.Label>
              <Form.Select {...fieldProps("discipline")} required>
                <option value="">Select profession</option>
                {disciplines.map((discipline) => <option key={discipline}>{discipline}</option>)}
              </Form.Select>
              {fieldError("discipline")}
            </Form.Group>
            <Form.Group className="mb-3" controlId="signup-practice-name">
              <Form.Label>Organisation</Form.Label>
              <Form.Control {...fieldProps("practice_name")} placeholder="Advance Rehab Centre" autoComplete="organization" maxLength={100} required />
              {fieldError("practice_name")}
              <Form.Text>Enter your organisation or practice name. Your trial workspace is private.</Form.Text>
            </Form.Group>
            <p className="small text-muted">We'll email you a secure link to set your password.</p>
            {captchaConfigError && <Alert variant="danger">
              Signup verification is unavailable. Please try again.
              {" "}<Button variant="link" onClick={() => setConfigAttempt((value) => value + 1)}>Retry verification</Button>
            </Alert>}
            {!captchaConfig && !captchaConfigError && <p role="status">Loading robot check…</p>}
            {captchaConfig?.captcha_required && <>
              <SignupCaptcha key={captchaAttempt} siteKey={captchaConfig.captcha_site_key} onChange={updateCaptcha} />
              {errors.captcha_token && <p role="alert" className="text-danger">{Array.isArray(errors.captcha_token) ? errors.captcha_token.join(" ") : errors.captcha_token}</p>}
            </>}
            <Button type="submit" className="w-100 trial-signup-submit" disabled={isSubmitting || !captchaConfig || (captchaConfig.captcha_required && !captchaToken)}>
              {isSubmitting ? "Creating your account…" : "Start free trial"}
            </Button>
          </fieldset>
        </Form></div></div>
      )}
      <footer className="trial-signup-footer">
        <p>Already have an account? <Link to="/">Log in</Link>.</p>
        <p>Are you a client? Ask your practitioner for an invitation.</p>
        <p>Want to trial Plasticise with others from your clinic? <a href="mailto:admin@plasticise.app?subject=Team%20trial%20of%20Plasticise">Send us a message</a>.</p>

      </footer>
      </section>
    </main>
  );
};

export default Signup;

